import type { MotionPropsFor, StackProps } from '@hover/blueprint';
import { Box, HStack, motion } from '@hover/blueprint';
import type { IconProp, PictogramProp } from '@hover/icons';
import { iconInspiration } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import React from 'react';
import { generatePath } from 'react-router';

import type { CustomizationCategory } from 'src/modules/homeownerApp/constants/customizationCategories';
import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import { useQueryParam } from 'src/shared/hooks';
import { Route } from 'src/shared/route';
import { hideScrollbars } from 'src/shared/utils/styles';

import { categoryToRouteParams } from '../../Customize/menu';

import { LandingPagePictogramButton } from './EmbedLandingPagePictogramButton';

export interface EmbedMenuItem {
  category?: null | CustomizationCategory;
  label: string;
  icon: IconProp;
  pictogram?: PictogramProp;
  disabled?: boolean;
}

interface EmbedCustomizeMenuProps extends MotionPropsFor<StackProps> {
  menu: EmbedMenuItem[];
  isLoading: boolean;
  targetImage: AiDisplayImageFragment;
  showMenu?: boolean;
}

const MotionHStack = motion(hideScrollbars(HStack));

const EmbedCustomizeMenu = ({
  isLoading,
  menu: menuProp,
  targetImage: image,
  showMenu = true,
  ...props
}: EmbedCustomizeMenuProps) => {
  const menu = isLoading ? menuProp.slice(0, 2) : menuProp;
  const isCarouselEnabled = useQueryParam('showCarousel') === 'true';

  return (
    <Box justifyContent="center" width="100%">
      <AnimatePresence>
        {showMenu && !isLoading && (
          <MotionHStack
            alignItems="flex-start"
            animate={{ opacity: 1, y: 0 }}
            data-testid="customize-menu"
            display={{ base: 'none', tablet: 'flex' }}
            exit={{ opacity: 0, y: 50 }}
            flexDirection="row"
            initial={{ opacity: 0, y: 50 }}
            justifyContent="start"
            // Not really how you're supposed to use this prop, but using image.id as key
            // causes a re-render on every time we change the image,
            // leading to unstable behavior when scrolling from one image to another.
            key="1"
            layout
            overflowX="auto"
            px={isCarouselEnabled ? undefined : { base: 400, tablet: 0 }}
            py="500"
            spacing="200"
            transition={{
              ease: 'easeOut',
            }}
            whiteSpace="nowrap"
            width="fit-content"
            {...props}
          >
            <LandingPagePictogramButton
              isLoading={isLoading}
              item={{
                label: 'Ideas',
                icon: iconInspiration,
              }}
              key="Ideas"
              toPath={{
                pathname: generatePath(
                  Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path,
                  {
                    aiDisplayImageId: image.id,
                  },
                ),
              }}
            />
            {menu.map(item => (
              <LandingPagePictogramButton
                isLoading={isLoading}
                item={item}
                key={item.label}
                toPath={{
                  pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE.path, {
                    aiDisplayImageId: image.id,
                    ...(item.category
                      ? categoryToRouteParams(item.category)
                      : undefined),
                  }),
                }}
              />
            ))}
          </MotionHStack>
        )}
      </AnimatePresence>
    </Box>
  );
};

export { EmbedCustomizeMenu };
